import { BBDTag, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Avatar, Select } from 'antd'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { chartColors } from '../constants'
import { DiffDisplay } from '../MetricGrid/Renderers/DifferenceCell'
import { createBarData, createComparisonRow, createMetricRows, existentAnalyticsItems, metricOptionSet } from '../util'
import { MetricHistoryChart } from './MetricHistoryChart'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { useFormatter } from '@api/useFormatter'
const { Option } = Select

export const MetricComparison = ({
  viewMetricBy,
  gridData,
  selectedRows,
  dataPoints,
  comparisonAverages,
  comparisonDataPoints,
  comparisonKey,
}) => {
  const [selectedMetricType, setSelectedMetricType] = useState('productivity')
  const [selectedMetric, setSelectedMetric] = useState('orders')
  const [barData, setBarData] = useState([])
  const [selectedMetricRows, setSelectedMetricRows] = useState([])
  const [comparisonRow, setComparisonRow] = useState()
  const [metricOptions, setMetricOptions] = useState(metricOptionSet.productivity)

  useEffect(() => {
    if (selectedRows.length !== 0) {
      const names = selectedRows.map((row) => row.name)
      setBarData(
        createBarData(names, dataPoints, selectedMetricType, selectedMetric, comparisonDataPoints, comparisonKey)
      )
      setSelectedMetricRows(createMetricRows(names, gridData, dataPoints, selectedMetricType, selectedMetric))
    } else {
      setSelectedMetricRows([])
      const lineData = comparisonDataPoints
        .filter(existentAnalyticsItems)
        .filter((point) => point?.name === comparisonAverages[0]?.name)
        .sort((a, b) => (moment(a.date).isBefore(b.date) ? -1 : 1))
        .map((match) => ({
          x: formatters.DATE(moment(match?.date)),
          y: match[selectedMetricType][selectedMetric],
        }))

      setBarData([{ id: comparisonAverages[0]?.name, color: '#c79c02', data: lineData }])
    }
    let comparisonItem
    if (comparisonKey === 'system' || selectedRows.length === 0) {
      comparisonItem = comparisonAverages[0]
    } else {
      comparisonItem = comparisonAverages.find((row) => row.name === selectedRows[0][comparisonKey])
    }

    setComparisonRow(createComparisonRow(comparisonItem, comparisonDataPoints, selectedMetricType, selectedMetric))
  }, [selectedMetric, comparisonAverages, selectedRows])

  useEffect(() => {
    setMetricOptions(metricOptionSet[selectedMetricType])
    setSelectedMetric(metricOptionSet[selectedMetricType][0].value)
  }, [selectedMetricType])

  if (!comparisonRow) return null
  return (
    <div className=' flex-1 bg-1 bordered round-corners vertical-flex'>
      <div className='flex flex-1 '>
        <div style={{ minWidth: '500px', maxHeight: 280 }} className='flex-1 vertical-flex border-right'>
          <div>
            <MetricSelect
              setSelectedMetric={setSelectedMetric}
              selectedMetric={selectedMetric}
              setSelectedMetricType={setSelectedMetricType}
              selectedMetricType={selectedMetricType}
              metricOptions={metricOptions}
            />
            <ComparisonRow row={comparisonRow} />
            <MetricItemsHeader viewMetricBy={viewMetricBy} />
          </div>

          <div className='vertical-flex flex-1' style={{ overflow: 'auto' }}>
            {selectedMetricRows.length === 0 ? (
              <div className='flex-1 vertical-flex justify-center items-center p-5'>
                <Texto align='center' category='p2' appearance='medium' weight={600}>
                  Select a metric row on the grid below to view the history comparison.{' '}
                </Texto>
              </div>
            ) : (
              <>
                {selectedMetricRows.map((row, i) => (
                  <MetricItemRow comparisonRow={comparisonRow} key={i} color={chartColors[i]} row={row} />
                ))}
              </>
            )}
          </div>
        </div>
        <Vertical flex={3} className='p-3'>
          <MetricHistoryChart
            selectedRows={selectedRows}
            barData={barData}
            selectedMetricType={selectedMetricType}
            selectedMetric={selectedMetric}
          />
        </Vertical>
      </div>
    </div>
  )
}
const MetricItemsHeader = ({ viewMetricBy }) => {
  return (
    <div className='flex-1 vertical-flex'>
      <div className='bg-2 flex px-4 py-2'>
        <div className='flex-1'>
          <Texto category='label' style={{ textTransform: 'uppercase' }} appearance='medium' weight='bold'>
            {viewMetricBy}
          </Texto>
        </div>
        <div className='flex-1'>
          <Texto align='right' category='label' style={{ whiteSpace: 'nowrap' }} appearance='medium' weight='bold'>
            AVERAGE
          </Texto>
        </div>
        <div className='flex-1'>
          <Texto style={{ whiteSpace: 'nowrap' }} align='right' category='label' appearance='medium' weight='bold'>
            RECENT
          </Texto>
        </div>
      </div>
    </div>
  )
}
const MetricItemRow = ({ row, comparisonRow, color }) => {
  const { name, recent, average } = row
  return (
    <div className='flex px-4 py-3 border-bottom'>
      <div className='flex-1 flex items-center'>
        <Avatar style={{ backgroundColor: color }} className='mr-2' size={15} />
        <Texto category='p2' weight='bold'>
          {name}
        </Texto>
      </div>
      <div className='flex-1 justify-end'>
        <DiffDisplay value={average} difference={row.average - comparisonRow.average} />
      </div>
      <div className='flex-1 flex justify-end'>
        <DiffDisplay value={recent} difference={row.recent - comparisonRow.recent} />
      </div>
    </div>
  )
}
const MetricSelect = ({
  selectedMetricType,
  setSelectedMetricType,
  selectedMetric,
  setSelectedMetric,
  metricOptions,
}) => {
  return (
    <div className='flex border-bottom p-3'>
      <div className='flex flex-1 pr-3 items-center'>
        <Texto category='label' appearance='medium' weight='bold' className='mr-4'>
          METRIC
        </Texto>
        <div className='flex-1 pr-3'>
          <Select
            style={{ width: '100%' }}
            onChange={(value) => setSelectedMetricType(value)}
            value={selectedMetricType}
          >
            <Option value='productivity'> Productivity</Option>
            <Option value='accuracy'> Accuracy</Option>
          </Select>
        </div>
        <div className='flex-1'>
          <Select style={{ width: '100%' }} onChange={(value) => setSelectedMetric(value)} value={selectedMetric}>
            {metricOptions.map((option, i) => (
              <Option key={i} value={option.value}>
                {option.display}
              </Option>
            ))}
          </Select>
        </div>
      </div>
    </div>
  )
}
const ComparisonRow = ({ row }) => {
  return (
    <div className='flex p-4 items-center'>
      <Texto className='flex-1' category='h6' weight='bold' textTransform={'capitalize'}>
        {row?.name}
      </Texto>
      <div className='flex flex-1 items-center justify-center'>
        <Texto category='label' appearance='medium' weight='bold' style={{ whiteSpace: 'nowrap' }} className='mr-4'>
          AVERAGE
        </Texto>
        <BBDTag className='gold-tag'>{row.average.toLocaleString('en')}</BBDTag>
      </div>
      <div className='flex flex-1 items-center justify-center'>
        <Texto category='label' appearance='medium' weight='bold' className='mr-4'>
          RECENT
        </Texto>
        <BBDTag className='gold-tag'>{row.recent.toLocaleString('en')}</BBDTag>
      </div>
    </div>
  )
}
